import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStore } from "../contexts/StoreContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchPasses } from "../api/queries";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  WalletIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { deletePassLayout } from "../api/mutations";
import EmptyState from "../components/EmptyState";
import DeleteAlert from "../components/DeleteAlert";
import TrialEndedAlert from "../components/TrialEndedAlert";
import { useState } from "react";
import NoPassPreviewImage from "../assets/no-pass-preview.png";
import Header from "../components/Header";
import Button from "../components/Button";
import { usePlanAlert } from "../contexts/PlanAlertContext";

export default function Passes() {
  const { selectedStore, canManageStore } = useStore();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [deletePassLayoutId, setDeletePassLayoutId] = useState(null);
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);
  const { showPlanAlert } = usePlanAlert();

  const {
    data: passes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const getPassLimit = () => {
    // Check if store is in trial period (15 days from creation)
    const isInTrialPeriod = () => {
      if (!selectedStore?.trial_expires_at) return false;
      const trialEndDate = new Date(selectedStore.trial_expires_at);
      trialEndDate.setDate(trialEndDate.getDate());
      return new Date() < trialEndDate;
    };

    // If subscription is not active and not in trial period, return 0
    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "trialing" &&
      selectedStore?.subscription_status !== "past_due" &&
      !isInTrialPeriod()
    )
      return 0;

    // During trial period, allow unlimited
    if (isInTrialPeriod()) return Infinity;

    // For active subscriptions, check plan limits
    switch (selectedStore?.current_plan) {
      case "starter":
        return 1;
      case "growth":
        return 4;
      case "enterprise":
        return Infinity;
      default:
        return 0;
    }
  };

  // Check if store is in trial period (15 days from creation)
  const isInTrialPeriod = () => {
    if (!selectedStore?.trial_expires_at) return false;
    const trialEndDate = new Date(selectedStore.trial_expires_at);
    trialEndDate.setDate(trialEndDate.getDate());
    return new Date() < trialEndDate;
  };

  const handleNewLayout = (passLayout) => {
    // Check subscription limits
    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "past_due" &&
      selectedStore?.subscription_status !== "trialing" &&
      !isInTrialPeriod()
    ) {
      setIsTrialEndedAlertOpen(true);
      return;
    }

    if (selectedStore?.features.pass_limit <= passes.length) {
      showPlanAlert({
        title: t("featureLimit.title"),
        description: t("featureLimit.description", {
          plan: selectedStore?.current_plan,
          limit: selectedStore?.features.pass_limit,
          feature: t("featureLimit.features.card", {
            count: selectedStore?.features.pass_limit,
          }),
        }),
      });
      return;
    }

    navigate("/passes/new");
  };

  const handleEditClick = (passLayout) => {
    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "trialing" &&
      selectedStore?.subscription_status !== "past_due" &&
      !isInTrialPeriod()
    ) {
      setIsTrialEndedAlertOpen(true);
      return;
    } else {
      navigate(`/passes/${passLayout.id}/edit`);
    }
  };

  const deletePassLayoutMutation = useMutation({
    mutationFn: deletePassLayout,
    onSuccess: () => {
      // Invalidate and refetch the passes query
      queryClient.invalidateQueries({
        queryKey: ["passes", selectedStore?.id],
      });
    },
  });

  const handleDelete = async (passId) => {
    if (window.confirm(t("passes.list.deleteConfirm"))) {
      try {
        await deletePassLayoutMutation.mutateAsync({
          storeId: selectedStore.id,
          passId: passId,
        });
      } catch (error) {
        console.error("Error deleting pass layout:", error);
      }
    }
  };

  if (isLoading) {
    return <div className="p-4">{t("passes.list.loading")}</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        {t("passes.list.error")}
        {error.message}
      </div>
    );
  }

  return (
    <>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        <Header
          icon={WalletIcon}
          title={t("passes.title")}
          description={t("passes.description")}
          rightSection={
            canManageStore && (
              <Button onClick={() => handleNewLayout()} icon={PlusIcon}>
                {t("passes.list.newButton")}
              </Button>
            )
          }
        />
        {passes?.length === 0 ? (
          <EmptyState />
        ) : (
          // sm:grid-cols-2 lg:grid-cols-3 xl:flex xl:flex-wrap
          <div className="grid sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 lg:gap-8">
            {passes.map((passLayout) => (
              <div
                key={passLayout.id}
                className="rounded-lg border border-gray-200 bg-white shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
              >
                <div className="bg-gray-200 pl-4 p-2 rounded-t-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex">{passLayout.name}</div>
                    {/* invisible, not hidden: the buttons set the header's
                        height, and a member's card must not shrink. */}
                    <div
                      className={`flex space-x-2 ${canManageStore ? "" : "invisible"}`}
                    >
                      <button
                        onClick={() => handleEditClick(passLayout)}
                        className="inline-flex items-center p-2 text-sm font-medium text-indigo-600 rounded-md hover:bg-indigo-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setIsDeleteAlertOpen(true);
                          setDeletePassLayoutId(passLayout.id);
                        }}
                        className="inline-flex items-center p-2 text-sm font-medium text-red-600 rounded-md hover:bg-red-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <img
                    src={passLayout.preview_image_url ?? NoPassPreviewImage}
                    alt="Pass Preview"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deletePassLayoutMutation.mutate({
            storeId: selectedStore.id,
            passId: deletePassLayoutId,
          });
          setIsDeleteAlertOpen(false);
        }}
      />
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </>
  );
}
